import React, { useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, PhoneIncoming } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import OutboundGuardDialogs from "@/components/compliance/OutboundGuardDialogs";
import { useOutboundGuard } from "@/hooks/useOutboundGuard";
import { OutboundGuardOptions } from "@/model/interface/Compliance";
import MainDialerContent from "@/components/dialer/components/MainDialerContent";
import CallerIDSelector from "@/components/dialer/components/CallerIDSelector";
import IncomingCallCard from "@/components/dialer/components/IncomingCallCard";
import {
  useGetOwnPhoneNumbersQuery,
  useHoldCallMutation,
  useGetTelephonySettingsQuery,
  useInitiateCallMutation,
  useUpdateCallerIdMutation,
  useLazyGetActiveCallQuery,
  useGetCallQuery,
} from "@/store/rest_api";
import DialerKeypad from "@/components/dialer/DialerKeypad";
import ActiveCall from "@/components/dialer/ActiveCall";
import { applyPreferredMicrophone } from "@/components/dialer/util/MicrophoneUtil";
import { DEVICE_PLATFORM } from "@/util/device";
import { useFeature } from "@/hooks/useFeature";
import { CallFlowType } from "@/model/interface/CallFlowType";
import CallDispositionForm from "@/components/dialer/components/CallDispositionForm";
import { toast } from "sonner";
import { Call } from "@twilio/voice-sdk";
import { ActiveCallInfo, useCallDialog } from "@/context/CallDialogContext";
import { IncomingCallEvent } from "@/model/interface/IncomingCallEvent";

interface DialerTabProps {
  isWideLayout?: boolean;
  onActiveCallChange?: (isActive: boolean) => void;
  onHeightChange?: (height: number) => void;
  answeredCall?: Call | null;
  incomingCall?: Call | null;
  incomingCallEvent?: IncomingCallEvent | null;
}

export default function DialerTab({
  isWideLayout = false,
  onActiveCallChange,
  onHeightChange,
  incomingCall,
  incomingCallEvent,
}: DialerTabProps) {
  const {
    setIncomingCall,
    setActiveCall,
    activeCall,
    activeCallInfo,
    setActiveCallInfo,
    heldCall,
    setHeldCall,
    device,
    pendingDialNumber,
    setPendingDialNumber,
    dialerDraft,
    setDialerDraft,
    selectedCallerId,
    setSelectedCallerId,
  } = useCallDialog();
  const { data: phoneNumbers, isLoading } = useGetOwnPhoneNumbersQuery();
  const { data: telephonySettings } = useGetTelephonySettingsQuery();
  const [updateCallerId] = useUpdateCallerIdMutation();
  const [getActiveCall] = useLazyGetActiveCallQuery();
  const [initiateCall] = useInitiateCallMutation();
  const [holdCall] = useHoldCallMutation();
  const hasCallControls = useFeature("CALL_CONTROLS");
  const { data: activeCallRecord } = useGetCallQuery(activeCallInfo?.callId ?? "", { skip: !activeCallInfo?.callId });
  const keypadInput = dialerDraft;
  const setKeypadInput = setDialerDraft;
  const isInCall = activeCall !== null;
  const canHoldActiveCall =
    hasCallControls && Boolean(activeCallInfo?.event) && activeCallRecord?.callFlow?.type === CallFlowType.ADVANCED;
  const [showKeypadInCall, setShowKeypadInCall] = useState(false);
  const switchingCountRef = useRef(0);
  const heldCallRef = useRef(heldCall);
  heldCallRef.current = heldCall;
  // The server's terminal call_status_update can clear activeCallInfo before the SDK's disconnect
  // event reaches us, which used to lose the wrap-up entirely. Keep the last known one to fall back on.
  const lastActiveCallInfoRef = useRef<ActiveCallInfo | null>(null);
  if (activeCallInfo) {
    lastActiveCallInfoRef.current = activeCallInfo;
  }
  const handleHangUpRef = useRef<() => void>(() => {});
  const registeredCallsRef = useRef(new WeakSet<Call>());
  const transferredRef = useRef(false);
  const [isSwapLoading, setIsSwapLoading] = useState(false);
  const [completedCallInfo, setCompletedCallInfo] = useState<{ callId: string; displayName: string } | null>(null);
  const outboundGuard = useOutboundGuard();

  // Errors propagate: the quiet-hours guard needs to see the server's refusal, so the toast lives
  // in handleCall rather than here.
  const placeCall = async (phoneNumber: string, options: OutboundGuardOptions) => {
    const call = await initiateCall({
      fromPhoneNumber: { id: selectedCallerId },
      phoneNumber,
      ...options,
    }).unwrap();

    await applyPreferredMicrophone(device!);
    const twilioCall = await device!.connect({
      params: {
        callId: call.id!,
        To: phoneNumber,
        From: selectedCallerId,
        platform: DEVICE_PLATFORM,
      },
    });

    setActiveCall(twilioCall);
    setActiveCallInfo({ callId: call.id!, displayName: phoneNumber, contactId: call.contact?.id, startedAt: Date.now() });
    onActiveCallChange?.(true);

    registeredCallsRef.current.add(twilioCall);
    twilioCall.on("disconnect", () => handleHangUp());
  };

  const rejoinHeldCall = async (callId: string) => {
    await applyPreferredMicrophone(device!);
    const twilioCall = await device!.connect({ params: { rejoinCall: callId, platform: DEVICE_PLATFORM } });
    setActiveCall(twilioCall);
    registeredCallsRef.current.add(twilioCall);
    twilioCall.on("disconnect", () => handleHangUpRef.current());
  };

  const handleCall = async (phoneNumber: string) => {
    if (!selectedCallerId) {
      toast.error("Please select a caller ID");
      return;
    }
    if (!device) {
      toast.error("Voice device not ready. Please wait or refresh.");
      return;
    }
    try {
      await outboundGuard.guard((options) => placeCall(phoneNumber, options));
    } catch (error: any) {
      toast.error(error?.detail || "Failed to initiate call");
    }
  };

  const handleAcceptIncoming = async () => {
    if (!incomingCall) return;

    const from = incomingCallEvent?.fromNumber ?? incomingCall.parameters.From ?? "Unknown";
    const displayName = incomingCallEvent?.contactName ?? from;
    let callId = incomingCallEvent?.callId || null;

    incomingCall.accept();

    if (!callId) {
      try {
        const result = await getActiveCall().unwrap();
        callId = result.id ?? null;
      } catch (e) {
        console.warn("Could not fetch active call", e);
      }
    }

    setActiveCall(incomingCall);
    setIncomingCall(null, null);
    setActiveCallInfo({
      callId: callId || "",
      displayName,
      contactId: incomingCallEvent?.contactId,
      event: { ...incomingCallEvent!, callId: callId || "" },
      startedAt: Date.now(),
    });
    onActiveCallChange?.(true);
    registeredCallsRef.current.add(incomingCall);
    incomingCall.on("disconnect", () => handleHangUp());
  };

  const handleRejectIncoming = () => {
    incomingCall?.reject();
    setIncomingCall(null, null);
  };

  const handleTransferPending = (isPending: boolean) => {
    transferredRef.current = isPending;
  };

  const handleTransferred = (targetName: string) => {
    toast.success(`Transferred to ${targetName}`);
  };

  const handleHangUp = () => {
    console.log("handleHangUp called", {
      switchingCount: switchingCountRef.current,
      activeCallId: activeCallInfo?.callId,
      heldCallId: heldCall?.callId,
      activeCallState: activeCall?.status(),
    });

    if (switchingCountRef.current > 0) {
      switchingCountRef.current--;
      console.log("handleHangUp: skipping (switching calls, remaining:", switchingCountRef.current, ")");
      return;
    }

    // Use ref to get the latest heldCall value (avoids stale closure from disconnect listener)
    const currentHeldCall = heldCallRef.current;
    const wasTransferred = transferredRef.current;
    transferredRef.current = false;

    // Save completed call info for disposition form (only if no held call to auto-resume)
    const endedCall = activeCallInfo ?? lastActiveCallInfoRef.current;
    if (!currentHeldCall && !wasTransferred && endedCall?.callId) {
      setCompletedCallInfo({ callId: endedCall.callId, displayName: endedCall.displayName });
    }
    lastActiveCallInfoRef.current = null;

    setActiveCall(null);
    setActiveCallInfo(null);
    setShowKeypadInCall(false);
    setKeypadInput("");

    if (currentHeldCall) {
      console.log("handleHangUp: active call ended, auto-resuming held call", currentHeldCall.callId);
      rejoinHeldCall(currentHeldCall.callId)
        .then(() => {
          setActiveCallInfo({
            callId: currentHeldCall.callId,
            displayName: currentHeldCall.event.contactName ?? currentHeldCall.event.fromNumber,
            contactId: currentHeldCall.event.contactId,
            event: currentHeldCall.event,
            startedAt: Date.now(),
          });
          onActiveCallChange?.(true);
          setHeldCall(null);
        })
        .catch(() => {
          toast.error("Failed to resume held call");
          setHeldCall(null);
          onActiveCallChange?.(false);
        });
    } else {
      onActiveCallChange?.(false);
    }
  };
  handleHangUpRef.current = handleHangUp;

  // Fallback: register disconnect handler for calls that weren't explicitly registered
  // (e.g., resumed calls auto-accepted in DashboardLayout)
  useEffect(() => {
    if (!activeCall || registeredCallsRef.current.has(activeCall)) return;
    registeredCallsRef.current.add(activeCall);
    const onDisconnect = () => handleHangUpRef.current();
    activeCall.on("disconnect", onDisconnect);
  }, [activeCall]);

  const handleHoldAndAnswer = async () => {
    console.log("HOLD", incomingCall, activeCallInfo, activeCall);

    if (!incomingCall || !activeCallInfo || !activeCall || !activeCallInfo.event) {
      return;
    }

    try {
      await holdCall(activeCallInfo.callId).unwrap();

      setHeldCall({
        call: activeCall,
        event: activeCallInfo.event!,
        callId: activeCallInfo.callId,
      });

      // The old active call's SDK connection will disconnect when we accept the new call.
      // We expect 1 stale disconnect event to ignore.
      switchingCountRef.current++;

      const from = incomingCallEvent?.fromNumber ?? incomingCall.parameters.From ?? "Unknown";
      const displayName = incomingCallEvent?.contactName ?? from;
      let callId = incomingCallEvent?.callId || null;

      if (!callId) {
        try {
          const result = await getActiveCall().unwrap();
          callId = result.id ?? null;
        } catch (e) {
          console.warn("Could not fetch active call", e);
        }
      }

      incomingCall.accept();
      setActiveCall(incomingCall);
      setIncomingCall(null, null);
      setActiveCallInfo({
        callId: callId || "",
        displayName,
        contactId: incomingCallEvent?.contactId,
        event: incomingCallEvent,
        startedAt: Date.now(),
      });

      registeredCallsRef.current.add(incomingCall);
      incomingCall.on("disconnect", () => handleHangUp());
    } catch (e) {
      toast.error("Failed to hold call");
    }
  };

  const handleEndAndAnswer = async () => {
    if (!incomingCall || !activeCall) {
      return;
    }
    switchingCountRef.current++;
    activeCall.disconnect();
    await handleAcceptIncoming();
  };

  const handleResume = async () => {
    if (!heldCall || isSwapLoading) {
      return;
    }

    try {
      // Hold the current active call first
      if (activeCallInfo) {
        await holdCall(activeCallInfo.callId).unwrap();

        // Store current call as the new held call
        const newHeld = {
          call: activeCall!,
          event: activeCallInfo.event!,
          callId: activeCallInfo.callId,
        };

        switchingCountRef.current++;
        activeCall?.disconnect();
        await rejoinHeldCall(heldCall.callId);

        setActiveCallInfo({
          callId: heldCall.callId,
          displayName: heldCall.event.contactName ?? heldCall.event.fromNumber,
          contactId: heldCall.event.contactId,
          event: heldCall.event,
          startedAt: Date.now(),
        });
        onActiveCallChange?.(true);
        setHeldCall(newHeld);
      } else {
        await rejoinHeldCall(heldCall.callId);

        setActiveCallInfo({
          callId: heldCall.callId,
          displayName: heldCall.event.contactName ?? heldCall.event.fromNumber,
          contactId: heldCall.event.contactId,
          event: heldCall.event,
          startedAt: Date.now(),
        });
        onActiveCallChange?.(true);
        setHeldCall(null);
      }
    } catch (e) {
      toast.error("Failed to resume call");
    } finally {
      setIsSwapLoading(false);
    }
  };

  // Seed from the number this user last called with, falling back to their first number only
  // when the server has no record and nothing is already selected.
  useEffect(() => {
    if (!phoneNumbers || phoneNumbers.length === 0 || selectedCallerId) {
      return;
    }
    const remembered = telephonySettings?.lastCallerIdPhoneNumberId;
    const isStillOwned = remembered && phoneNumbers.some((number) => number.id === remembered);
    setSelectedCallerId(isStillOwned ? remembered : phoneNumbers[0].id!!);
  }, [phoneNumbers, selectedCallerId, telephonySettings, setSelectedCallerId]);

  const handleCallerIdChange = (phoneNumberId: string) => {
    setSelectedCallerId(phoneNumberId);
    updateCallerId({ phoneNumberId });
  };

  useEffect(() => {
    if (pendingDialNumber && selectedCallerId && !isInCall) {
      const number = pendingDialNumber;
      setPendingDialNumber(null);
      handleCall(number);
    }
  }, [pendingDialNumber, selectedCallerId, isInCall]);

  useEffect(() => {
    if (completedCallInfo) {
      onHeightChange?.(650);
    }
  }, [completedCallInfo, onHeightChange]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-6 text-white">Make a call</h2>
          <div className="relative mb-6">
            <Input placeholder="Loading..." disabled />
          </div>
        </div>
        <div className="pt-6 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white">Caller ID</span>
            <Select disabled>
              <SelectTrigger className="w-70">
                <SelectValue placeholder="Loading..." />
              </SelectTrigger>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  if (incomingCall && incomingCallEvent && !isInCall) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-8 py-8">
        <IncomingCallCard event={incomingCallEvent} />

        <div className="flex justify-center gap-12">
          <div className="flex flex-col items-center gap-2">
            <Button
              size="icon"
              className="h-16 w-16 rounded-full bg-red-600 hover:bg-red-700 text-white"
              onClick={handleRejectIncoming}
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
            <span className="text-xs text-muted-foreground">Decline</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Button
              size="icon"
              className="h-16 w-16 rounded-full bg-green-600 hover:bg-green-700 text-white"
              onClick={handleAcceptIncoming}
            >
              <PhoneIncoming className="h-6 w-6" />
            </Button>
            <span className="text-xs text-muted-foreground">Accept</span>
          </div>
        </div>
      </div>
    );
  }

  if (completedCallInfo && !isInCall) {
    return (
      <CallDispositionForm
        callId={completedCallInfo.callId}
        displayName={completedCallInfo.displayName}
        onComplete={() => setCompletedCallInfo(null)}
      />
    );
  }

  if (isInCall) {
    return (
      <div className="space-y-4 h-full flex flex-col">
        {incomingCall && incomingCallEvent && (
          <div className="flex items-center justify-between px-4 py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-center gap-3">
              <PhoneIncoming className="h-4 w-4 text-yellow-500 animate-pulse" />
              <div>
                <p className="text-sm font-medium dark:text-white">
                  {incomingCallEvent.contactName ?? incomingCallEvent.fromNumber}
                </p>
                <p className="text-xs text-muted-foreground">
                  {incomingCallEvent.transferredByName
                    ? `Transfer from ${incomingCallEvent.transferredByName}`
                    : "Incoming call"}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-600 hover:bg-red-600 hover:text-white"
                onClick={handleRejectIncoming}
              >
                <PhoneOff className="h-3 w-3" />
              </Button>
              {canHoldActiveCall ? (
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={handleHoldAndAnswer}>
                  Hold & Answer
                </Button>
              ) : (
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={handleEndAndAnswer}>
                  End & Answer
                </Button>
              )}
            </div>
          </div>
        )}

        {heldCall && (
          <div className="flex items-center justify-between px-4 py-3 bg-muted border rounded-lg">
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium dark:text-white">
                  {heldCall.event.contactName ?? heldCall.event.fromNumber}
                </p>
                <p className="text-xs text-muted-foreground">On hold</p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90 text-white"
              onClick={handleResume}
              disabled={isSwapLoading}
            >
              {isSwapLoading ? "Switching..." : "Resume"}
            </Button>
          </div>
        )}

        <div className="flex-1">
          {showKeypadInCall ? (
            <Card className="p-4">
              <DialerKeypad
                value={keypadInput}
                onChange={setKeypadInput}
                onCall={() => {}}
                disabled={true}
                inCallMode={true}
              />
              <Button variant="outline" className="w-full mt-4" onClick={() => setShowKeypadInCall(false)}>
                Hide Keypad
              </Button>
            </Card>
          ) : (
            <ActiveCall
              callId={activeCallInfo?.callId ?? null}
              contactId={activeCallInfo?.contactId}
              contactType={activeCallInfo?.event?.context?.contactType}
              twilioCall={activeCall}
              displayName={activeCallInfo?.displayName ?? "Unknown"}
              startedAt={activeCallInfo?.startedAt ?? Date.now()}
              isInbound={Boolean(activeCallInfo?.event)}
              onHangPressed={handleHangUp}
              onKeypadPressed={() => setShowKeypadInCall(true)}
              onTransferPending={handleTransferPending}
              onTransferred={handleTransferred}
            />
          )}
        </div>

        {!showKeypadInCall && (
          <CallerIDSelector
            phoneNumbers={phoneNumbers}
            selectedCallerId={selectedCallerId}
            onCallerIdChange={handleCallerIdChange}
          />
        )}
      </div>
    );
  }

  if (isWideLayout) {
    return (
      <div className="grid grid-cols-[1fr_250px] gap-4 h-full">
        <OutboundGuardDialogs guard={outboundGuard} actionLabel="Call anyway" />
        <div className="min-w-0">
          <MainDialerContent
            keypadInput={keypadInput}
            onKeypadInputChange={setKeypadInput}
            onCall={handleCall}
            onHeightChange={onHeightChange}
          />
        </div>
        <div className="min-w-0">
          <CallerIDSelector
            phoneNumbers={phoneNumbers}
            selectedCallerId={selectedCallerId}
            onCallerIdChange={handleCallerIdChange}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-full flex flex-col">
      <OutboundGuardDialogs guard={outboundGuard} actionLabel="Call anyway" />
      <div className="flex-1 min-h-0">
        <MainDialerContent
          keypadInput={keypadInput}
          onKeypadInputChange={setKeypadInput}
          onCall={handleCall}
          onHeightChange={onHeightChange}
        />
      </div>
      <div className="shrink-0">
        <CallerIDSelector
          phoneNumbers={phoneNumbers}
          selectedCallerId={selectedCallerId}
          onCallerIdChange={handleCallerIdChange}
        />
      </div>
    </div>
  );
}
